import React, { useState } from 'react';
import styles from './UserList.module.css';
import UserTable from '../UserTable/UserTable';
import UserCard from '../UserCard/UserCard';
import Button from '../../common/Button/Button';
import Spinner from '../../common/Spinner/Spinner';
import { usePermission } from '../../../hooks/usePermissions';

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const UserList = ({
  users = [],
  loading = false,
  roles = [],
  onEdit,
  onDelete,
  onView,
  onSearch,
  onRefresh,
  onAddUser,
  children
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [raFilter, setRaFilter] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const { hasPermission } = usePermission();

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const triggerSearch = (term, role, ra, from, to) => {
    if (onSearch) {
      onSearch(term, { role, isresearchanalyst: ra, createdFrom: from, createdTo: to });
    }
  };

  const handleSearchClick = () => {
    triggerSearch(searchInput, roleFilter, raFilter, createdFrom, createdTo);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleRoleChange = (e) => {
    setRoleFilter(e.target.value);
    triggerSearch(searchInput, e.target.value, raFilter, createdFrom, createdTo);
  };

  const handleRaChange = (e) => {
    setRaFilter(e.target.value);
    triggerSearch(searchInput, roleFilter, e.target.value, createdFrom, createdTo);
  };
  
  const handleCreatedFromChange = (e) => {
    setCreatedFrom(e.target.value);
    triggerSearch(searchInput, roleFilter, raFilter, e.target.value, createdTo);
  };

  const handleCreatedToChange = (e) => {
    setCreatedTo(e.target.value);
    triggerSearch(searchInput, roleFilter, raFilter, createdFrom, e.target.value);
  };

  const clearSearch = () => {
    setSearchInput('');
    setRoleFilter('');
    setRaFilter('');
    setCreatedFrom('');
    setCreatedTo('');
    triggerSearch('', '', '', '', '');
  };

  // Local filter is removed because filtering now happens on the backend across all pages.
  const filteredUsers = users;

  if (loading && users.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" text="Loading users..." />
      </div>
    );
  }

  return (
    <div className={styles.userListContainer}>
      <div className={styles.userListHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.listTitle}>User Management</h1>
          <p className={styles.userCount}>
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
          </p>
        </div>

        <div className={styles.headerActions}>
          {hasPermission("CREATE_USERSLIST") && (
            <button
              className={styles.btnAdd}
              onClick={onAddUser}
            >
              + Add New User
            </button>
          )}
        </div>
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.dropdownFilters}>
          <select 
            value={roleFilter} 
            onChange={handleRoleChange}
            className={styles.filterSelect}
          >
            <option value="">All Roles</option>
            {roles.map((role) => (
              <option key={role.id} value={String(role.id)}>
                {role.role_type}
              </option>
            ))}
          </select>

          <select 
            value={raFilter} 
            onChange={handleRaChange}
            className={styles.filterSelect}
          >
            <option value="">Res Analyst</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
          
          <input
            type="date"
            value={createdFrom}
            onChange={handleCreatedFromChange}
            className={styles.filterSelect}
            title="Created From"
          />
          <input
            type="date"
            value={createdTo}
            onChange={handleCreatedToChange}
            className={styles.filterSelect}
            title="Created To"
          />
        </div>

        <div className={styles.searchSection}>
          <div className={styles.imSearchWrap}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search users by name, email, phone..."
              value={searchInput}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
            />
          </div>
          <button
            type="button"
            className={styles.imBtnSearch}
            onClick={handleSearchClick}
          >
            Search
          </button>
          {searchInput && (
            <button
              type="button"
              className={styles.imBtnSearch}
              onClick={clearSearch}
            >
              Clear
            </button>
          )}
        </div>
      </div>


      {loading && users.length > 0 && (
        <div className={styles.loadingOverlay}>
          <Spinner text="Updating..." />
        </div>
      )}

      <div className={styles.bkTableWrap}>
        <div className={styles.userListContent}>
          {filteredUsers.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>👥</div>
              <h3 className={styles.emptyTitle}>
                {(searchInput || roleFilter || raFilter || createdFrom || createdTo) ? 'No users found' : 'No users available'}
              </h3>
              <p className={styles.emptyMessage}>
                {(searchInput || roleFilter || raFilter || createdFrom || createdTo)
                  ? `No users match your filters. Try adjusting your search or filters.`
                  : 'There are no users to display at the moment.'
                }
              </p>
              {(searchInput || roleFilter || raFilter || createdFrom || createdTo) && (
                <Button variant="outline" onClick={clearSearch}>
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <>
              {viewMode === 'table' ? (
                <>
                  <UserTable
                    users={filteredUsers}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onView={onView}
                    canEdit={hasPermission("MODIFY_USERSLIST")}
                      canDelete={hasPermission("DELETE_USERSLIST")}
                      viewKyc={hasPermission("VIEW_USERKYC")}
                  />
                </>
              ) : (
                <div className={`${styles.userGrid} ${styles[viewMode]}`}>
                  {filteredUsers.map((user) => {
                    return (
                      <UserCard
                        key={user.userid}
                        user={user}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onView={onView}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

export default UserList;